import { FormControl } from '@ui/molecules';
import { Platform } from 'react-native';
import { AutoHeightInput, Button, Checkbox, FilePicker, Input, Ionicons, Select, Text, View } from '@ui/atoms';
import { FILE_MIMES } from '@constants/MimeType';
import { FormattedMessage } from 'react-intl';
import { useState, useMemo } from 'react';
import useToast from '@hooks/useToast';
import useThemeColor from '@hooks/useThemeColor';
import useUploadMutation from '@hooks/mutations/useUploadMutation';
import { useFormik } from 'formik';
import { bool, mixed, object, string } from '@utils/yup';
import apiErrorsToFormik from '@utils/apiErrorsToFormik';
import useTranslate from '@hooks/useTranslate';
import SURVEY_TYPE from '@constants/SurveyType';
import { array } from 'yup';
import useCreateSurveyMutation from '@hooks/mutations/useCreateSurveyMutation';
import useUpdateSurveyMutation from '@hooks/mutations/useUpdateSurveyMutation';
import PropTypes from 'prop-types';
import CustomPropTypes from '@utils/CustomPropTypes';
import ChoicesList from './ChoicesList';

function SurveyForm({ onSuccess, survey }) {
  const { t } = useTranslate();
  const toast = useToast();
  const white = useThemeColor('$common.white');

  const { mutateAsync: createSurvey } = useCreateSurveyMutation();
  const { mutateAsync: updateSurvey } = useUpdateSurveyMutation(survey?.id);

  const [loading, setLoading] = useState(false);

  const initialValues = useMemo(
    () => ({
      title: survey?.title || '',
      content: survey?.content || '',
      file: survey?.file || null,
      duration: survey?.duration || null,
      type: survey?.type || SURVEY_TYPE.choices,
      answer_editable: survey?.answer_editable || false,
      realtime_results: survey?.realtime_results || false,
      // Choices survey
      choices: [
        {
          id: '51387c3e-acf8-4ae2-90e6-02afa2937625', // temp ID
          name: '',
          image: null,
        },
        {
          id: '268ae010-7ae3-472f-83f7-53ce9236abac',
          name: '',
          image: null,
        },
      ],
      single_choice: survey?.single_choice || false,
    }),
    [survey]
  );

  const hasAnswers = useMemo(() => Boolean(survey?.answers?.length), [survey]);

  const form = useFormik({
    enableReinitialize: true,
    validateOnMount: true, // true
    initialValues,
    validationSchema: object({
      title: string().required(),
      content: string().required(),
      file: mixed().nullable(),
      duration: mixed().required(),
      type: string().required(),
      answer_editable: bool().required(),
      realtime_results: bool().required(),

      // Choices survey
      choices: mixed().when('type', {
        is: SURVEY_TYPE.choices,
        then: array()
          .of(
            object({
              name: string().required(),
            })
          )
          .min(2)
          .required(),
        otherwise: array().required(),
      }),
      single_choice: bool().required(),
    }),
    onSubmit: async (data, { setErrors }) => {
      try {
        setLoading(true);
        const payload = {
          ...data,
          file: data.file?.id || null,
          choices:
            data.type === SURVEY_TYPE.choices
              ? data.choices.map((choice) => ({
                  ...choice,
                  id: survey ? choice.id : undefined,
                  image: choice.image?.id || null,
                }))
              : undefined,
          single_choice: data.type === SURVEY_TYPE.choices ? data.single_choice : undefined,
        };

        const res = survey ? await updateSurvey(payload) : await createSurvey(payload);

        onSuccess?.(res.data);
      } catch (err) {
        if (err.response?.status === 422) {
          apiErrorsToFormik(err.response.data.errors, setErrors);
          toast.error(t('errors.checkData'));
        } else {
          toast.error(t('errors.api.default'));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const { mutate: uploadFile, isLoading: uploadingFile } = useUploadMutation({
    onSuccess: (res) => form.setFieldValue('file', res.data),
    onError: (err) => {
      if (err.response?.status === 422) {
        toast.error(err.response.data.errors?.file[0]);
      } else {
        toast.error(t('errors.api.default'));
      }
    },
  });

  const handleSubmit = () => {
    form.submitForm();
    if (!form.isValid) toast.error(t('errors.checkData'));
  };

  const onSubmitEditing = Platform.select({ web: handleSubmit });

  const handleDeleteFile = () => form.setFieldValue('file', null);

  const types = Object.values(SURVEY_TYPE).map((type) => ({
    value: type,
    label: t('constants.SurveyType', { value: type }),
  }));

  const durations = [1, 3, 7, 14, 28].map((duration) => ({
    value: duration,
    label: t('ui.SurveyForm.duration.option', { duration }),
  }));

  return (
    <>
      <FormControl
        label={t('ui.SurveyForm.title')}
        sx={{ mb: '$4' }}
        error={form.touched.title && form.errors.title}
        required
      >
        <Input
          value={form.values.title}
          maxLength={100}
          withCharCounter
          onChangeText={form.handleChange('title')}
          onBlur={form.handleBlur('title')}
          onSubmitEditing={onSubmitEditing}
        />
      </FormControl>
      <FormControl
        label={t('ui.SurveyForm.content')}
        sx={{ mb: '$4' }}
        error={form.touched.content && form.errors.content}
        required
      >
        <AutoHeightInput
          minHeight={100}
          maxLength={600}
          withCharCounter
          value={form.values.content}
          onChangeText={form.handleChange('content')}
          onBlur={form.handleBlur('content')}
        />
      </FormControl>
      <FormControl
        label={t('ui.SurveyForm.duration.label')}
        error={form.touched.duration && form.errors.duration}
        required
        sx={{ mb: '$4' }}
      >
        <Select
          placeholder={{ label: t('common.verb.select'), value: '' }}
          items={durations}
          value={form.values.duration}
          onValueChange={(e) => {
            form.setFieldValue('duration', Number.parseInt(e, 10));
          }}
        />
      </FormControl>
      {!hasAnswers && (
        <>
          <FormControl
            label={t('ui.SurveyForm.file')}
            sx={{ mb: '$4' }}
            labelProps={{ variant: 'subtitle', sx: { color: '$primary.main' } }}
            error={form.touched.file && form.errors.file}
          >
            <FilePicker
              file={form.values.file}
              loading={uploadingFile}
              onChange={uploadFile}
              mimeTypes={FILE_MIMES}
              onDelete={handleDeleteFile}
              error={form.errors.file && form.touched.file}
            />
          </FormControl>
          <Text variant="subtitle2" sx={{ mt: '$2', mb: '$4', color: '$primary.main' }}>
            <FormattedMessage id="ui.SurveyForm.settings" />
          </Text>
          <FormControl
            label={t('ui.SurveyForm.type')}
            error={form.touched.type && form.errors.type}
            required
            sx={{ mb: '$4' }}
          >
            <Select
              placeholder={{ label: t('common.verb.select'), value: '' }}
              items={types}
              value={form.values.type}
              onValueChange={(e) => form.setFieldValue('type', e)}
            />
          </FormControl>
          {SURVEY_TYPE.choices === form.values.type && (
            <View sx={{ mb: '$4' }}>
              <ChoicesList
                choices={form.values.choices}
                errors={form.errors?.choices}
                touched={form.touched?.choices}
                setValues={form.setValues}
              />
            </View>
          )}
          <View sx={{ mb: '$4', mt: '$2' }}>
            <Checkbox
              checked={form.values.answer_editable}
              onChange={(e) => form.setFieldValue('answer_editable', e)}
              label={
                <Text variant="subtitle">
                  <FormattedMessage id="ui.SurveyForm.answerEditable.label" />
                </Text>
              }
            />
            <Text sx={{ mt: '$2' }}>
              <FormattedMessage id="ui.SurveyForm.answerEditable.help" />
            </Text>
          </View>
          {SURVEY_TYPE.choices === form.values.type && (
            <View sx={{ mb: '$4' }}>
              <Checkbox
                checked={form.values.single_choice}
                onChange={(e) => form.setFieldValue('single_choice', e)}
                label={
                  <Text variant="subtitle">
                    <FormattedMessage id="ui.SurveyForm.singleChoice.label" />
                  </Text>
                }
              />
              <Text sx={{ mt: '$2' }}>
                <FormattedMessage id="ui.SurveyForm.singleChoice.help" />
              </Text>
            </View>
          )}
          <View sx={{ mb: '$4' }}>
            <Checkbox
              checked={form.values.realtime_results}
              onChange={(e) => form.setFieldValue('realtime_results', e)}
              label={
                <Text variant="subtitle">
                  <FormattedMessage id="ui.SurveyForm.realtimeResults.label" />
                </Text>
              }
            />
            <Text sx={{ mt: '$2' }}>
              <FormattedMessage id="ui.SurveyForm.realtimeResults.help" />
            </Text>
          </View>
        </>
      )}
      <View sx={{ mt: '$2' }}>
        <Button
          color="primary"
          title={t('common.verb.publish')}
          onPress={() => handleSubmit()}
          startIcon={<Ionicons name="paper-plane-outline" size={15} color={white} />}
          loading={loading}
        />
      </View>
    </>
  );
}

SurveyForm.propTypes = {
  onSuccess: PropTypes.func,
  survey: CustomPropTypes.survey,
};

export default SurveyForm;
